import { useState, useCallback, useRef } from 'react';
import { AuthApi } from '@/services/authApi';
import { pollingService } from '@/services/pollingService';
import { DeviceCodeResponse, AuthTokens } from '@/types/auth.types';
import { useMsal } from '@azure/msal-react';
import { MsalTokenService } from '@/services/msalTokenService';
import { JWTDecoder } from '@/types/jwt.types';
import { TokenStorageService } from '@/services/tokenStorageService';

interface UseDeviceFlowReturn {
  deviceInfo: DeviceCodeResponse | null;
  polling: boolean;
  error: string | null;
  progressMessage: string;
  initiateAuth: () => Promise<void>;
  cancelAuth: () => void;
}

export const useDeviceFlow = (): UseDeviceFlowReturn => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceCodeResponse | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState('');
  const isInitiated = useRef(false);
  const { instance } = useMsal();

  const initiateAuth = useCallback(async () => {
    if (isInitiated.current) {
      console.warn('Authentication already initiated');
      return;
    }

    setError(null);
    setProgressMessage('Requesting device code...');
    isInitiated.current = true;

    try {
      console.log('Requesting device code from backend...');
      const response = await AuthApi.requestDeviceCode();
      
      console.log('Device code received:', {
        userCode: response.user_code,
        deviceCode: response.device_code.substring(0, 8) + '...',
        verificationUri: response.verification_uri
      });

      setDeviceInfo(response);
      setPolling(true);
      setProgressMessage('Waiting for authentication...');
      
      // Start polling AWS Lambda
      await pollingService.startPolling(
        response.device_code,
        response.interval,
        {
          onSuccess: async (tokens: AuthTokens) => {
            console.log('Device flow authentication successful - received Azure AD tokens');
            setPolling(false);
            setProgressMessage('Authentication successful!');
            
            // Extract user info from ID token and store using TokenStorageService
            if (tokens.idToken) {
              try {
                const userInfo = JWTDecoder.extractUserInfo(tokens.idToken);
                if (userInfo) {
                  // Store using the proper TokenStorageService
                  TokenStorageService.storeAuthData(tokens, userInfo, 'device');
                  
                  // Initialize MSAL with the device flow tokens for seamless refresh
                  const account = MsalTokenService.initializeWithDeviceFlowTokens(instance, tokens);
                  if (account) {
                    console.log('MSAL initialized with device flow tokens for automatic refresh');
                  } else {
                    console.warn('Could not initialize MSAL with tokens, but tokens are still stored');
                  }
                } else {
                  console.error('Failed to extract user info from ID token');
                }
              } catch (decodeError) {
                console.error('Could not decode ID token:', decodeError);
                // Continue anyway but don't store invalid data
              }
            } else {
              console.error('No ID token received from device flow');
            }
            
            // Refresh the current page to show authenticated state
            window.location.reload();
          },
          onError: (errorMessage: string) => {
            console.error('Device flow authentication failed:', errorMessage);
            setPolling(false);
            setError(errorMessage);
            setProgressMessage('');
            isInitiated.current = false;
          },
          onProgress: (message: string) => {
            setProgressMessage(message);
          }
        }
      );
      
    } catch (err: any) {
      console.error('Failed to initiate device flow:', err);
      setError(err.message || 'Failed to initiate authentication');
      setProgressMessage('');
      isInitiated.current = false;
    }
  }, []);

  const cancelAuth = useCallback(() => {
    console.log('Cancelling device flow authentication');
    pollingService.stopPolling();
    setPolling(false);
    setDeviceInfo(null);
    setError(null);
    setProgressMessage('');
    isInitiated.current = false;
  }, []);

  return {
    deviceInfo,
    polling,
    error,
    progressMessage,
    initiateAuth,
    cancelAuth
  };
};